import React, { useState } from "react";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import api from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";

export const LEGAL_CODE = "founder";

export const TARGET_META = {
  sportclips: { label: "Sport Clips" },
  chatters: { label: "Chatters" },
  newzealand: { label: "New Zealand" },
  justcuts: { label: "Just Cuts" },
};

// Segmented control that flips the platform's pitch target (Sport Clips <-> Chatters).
// Requires corporate role server-side; the UI is only ever shown behind CodeGate.
export function TargetSwitch() {
  const { branding, refresh } = useBranding();
  const current = branding.target || "sportclips";
  const [busy, setBusy] = useState(false);

  const set = async (t) => {
    if (t === current || busy) return;
    setBusy(true);
    try {
      await api.put("/branding/target", { target: t });
      await refresh();
      toast.success(`Pitch target switched to ${TARGET_META[t].label}`);
    } catch {
      toast.error("Corporate access required to switch target");
    }
    setBusy(false);
  };

  return (
    <div data-testid="target-switch" className="inline-flex rounded-lg border border-[#2A3550] bg-[#1E2638] p-1">
      {Object.entries(TARGET_META).map(([key, m]) => (
        <button
          key={key}
          data-testid={`target-switch-${key}`}
          disabled={busy}
          onClick={() => set(key)}
          className={`px-4 py-1.5 text-sm rounded-md transition-colors duration-200 disabled:opacity-60 ${current === key ? "text-white" : "text-slate-400 hover:text-white"}`}
          style={current === key ? { backgroundColor: "var(--brand)" } : {}}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

// Reveals its children only after the correct access code is entered.
export function CodeGate({ children, testidPrefix = "gate" }) {
  const [code, setCode] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [err, setErr] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (code.replace(/\s+/g, "").toLowerCase() === LEGAL_CODE.toLowerCase()) { setUnlocked(true); setErr(false); }
    else setErr(true);
  };

  if (unlocked) return children;

  return (
    <form onSubmit={submit} className="flex items-center gap-2 flex-wrap" data-testid={`${testidPrefix}-form`}>
      <input
        type="password" value={code} onChange={(e) => { setCode(e.target.value); setErr(false); }}
        autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false}
        data-testid={`${testidPrefix}-code-input`} placeholder="Access code"
        className="rounded-lg border border-[#2A3550] bg-[#0E131F] px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-[#38BDF8] focus:outline-none"
      />
      <button type="submit" data-testid={`${testidPrefix}-unlock-btn`}
        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity duration-200" style={{ backgroundColor: "var(--brand)" }}>
        <Lock className="h-3.5 w-3.5" /> Unlock
      </button>
      {err && <span className="text-xs text-red-400" data-testid={`${testidPrefix}-error`}>Incorrect code.</span>}
    </form>
  );
}
